import { useState, useEffect } from 'react';

import { getModalStyle } from '@/utils/Modal';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import IconButton from '@mui/material/IconButton';
import Button from '@/components/Button';
import { 
    Close as CloseIcon,     
    Save as SaveIcon,
    Refresh as ResetIcon
} from '@mui/icons-material';

import { getTableConfig, setTableConfig, removeTableConfig } from '@/utils/Table';

import Switch from '@/components/Switch';
import Slider from '@mui/material/Slider';
import { SliderMarkLabel } from "@mui/material";
import { getSliderStyle } from '@/utils/Styles';

import { checkCanFit } from '@/utils/Columns';

import { getColors, getDarkMode } from '@/utils/Settings';

// to config the table
export default function ConfigModal({
    t, open, setOpen, user, api, editable, table, config,
    columns, modal, sendMessage, enableAdd, enableEdit, fullScreen, headerFixed
}){

    const { lightColor, darkColor } = getColors();
    const darkmode = getDarkMode();

    const { sxDialog/*, sxCloseIcon*/ } = getModalStyle();
    const { sxSlider } = getSliderStyle();

    const handleCloseModal = () => setOpen(false);

    const [ visibility, setVisibility ] = useState({});

    const [ modalConfig, setModalConfig ] = useState(null);

    const [ fitConfig, setFitConfig ] = useState(null);

    const [ headerFixedConfig, setHeaderFixedConfig ] = useState(headerFixed ? 1 : 0);
    
    // for the columns width
    const [ sizes, setSizes ] = useState({});

    const [ defaultSize ] = useState(0); // auto
    const [ shiftStepSize ] = useState(30);
    const [ stepSize ] = useState(10);
    const [ minSize ] = useState(0);
    const [ maxSize ] = useState(1000);
    
    const marksSize = [
        {value: 0,label: t('actions.config.autosize')},
        {value: 1000,label: 1000},
    ];

    // for the row height

    // to indicate if row auto or concrete size. for all the rows
    const [ rowsHeight, setRowsHeight ] = useState(null);
    const [ defaultRowsHeight ] = useState(0); // auto

    const [ shiftStepRowsHeight ] = useState(20);
    const [ stepRowsHeight ] = useState(10);
    const [ minRowsHeight ] = useState(0);
    const [ maxRowsHeight ] = useState(200);

    const marksRowsHeight = [
        {value: 0,label: t('actions.config.autoheight')},
        {value: 200,label: 200},
    ];

    // to indicate per column, if adjust height to content
    const [ autoHeights, setAutoHeights ] = useState({});

    // check saved configuration
    useEffect(() => {

        var savedConfig = getTableConfig(user,table);

        // modal. if nothing saved, use value from server
        var savedModal = savedConfig?.modal;
        if(typeof savedModal === 'undefined' || savedModal === null){
            savedModal = modal ? 1 : 0;
        }
        setModalConfig(savedModal);
        
        // ['id': true,'name' : false,...]
        var savedVisibility = savedConfig?.columnVisibility;
              
        // if no config saved or empty array, show all (at least 1 must be visible)
        var visibilityData = {};        

        columns && Object?.keys(columns)?.map((column) => {

            var visible = 1;
            
            // check if visibility on config
            if(savedVisibility && Object.hasOwn(savedVisibility,column)){
                visible = savedVisibility[column];
            }
            else{
                // check parameter visible from backend
                if(Object.hasOwn(columns[column],'visible')){
                    visible = columns[column].visible;
                }
            }

            visibilityData[column] = visible;
        });

        setVisibility(visibilityData);

        // fit to screen. if nothing saved, use value from server
        let savedFit;
        if(savedConfig && Object.hasOwn(savedConfig,'fit')){
            savedFit = savedConfig.fit ? 1 : 0;
        }
        else{

            // check info from server
            if(config && Object.hasOwn(config,'fit')){
                savedFit = config?.fit ? 1 : 0;
            }
            else{
                savedFit = 0; // do not fit
            }
        }

        setFitConfig(savedFit);

        // sizes
        var savedSizes = savedConfig?.columnSize;
        var sizesData = {};
        columns && Object?.keys(columns)?.map((column) => {

            var size = 0;
            
            // if no info saved, check if width from server
            if(savedSizes && Object.hasOwn(savedSizes,column)){
                size = savedSizes[column];
            }
            else{

                // check info from server
                if(Object.hasOwn(columns[column],'width')){
                    size = columns[column]?.width;                    
                }
                else{
                    size = 0; // auto
                }
            }

            sizesData[column] = size;
        });

        setSizes(sizesData);

        // fix header. if nothing saved, use value from server
        /*
        let savedHeaderFixed;
        if(savedConfig && Object.hasOwn(savedConfig,'headerFixed')){
            savedHeaderFixed = savedConfig.headerFixed ? 1 : 0;
        }
        else{

            // check info from server
            if(config && Object.hasOwn(config,'headerFixed')){
                savedHeaderFixed = config?.headerFixed ? 1 : 0;
            }
            else{
                savedHeaderFixed = 0; // do not fixe
            }
        }

        setHeaderFixedConfig(savedHeaderFixed);
        */

        // heights

        // generic height for all the rows    
        var height = 0; // auto
            
        // if no info saved, check if rowsheight from server
        if(savedConfig && Object?.hasOwn(savedConfig,'rowsHeight')){
            height = savedConfig.rowsHeight ? savedConfig.rowsHeight : 0;
        }
        else{

            // check info from server
            if(config && Object.hasOwn(config,'rowsheight')){
                height = config['rowsheight'];                    
            }
            else{
                height = 0; // auto
            }
        }

        setRowsHeight(height);

        // adjust the row height to content or not for the columns
        var savedAutoHeights = savedConfig?.columnAutoHeight;
        var autoHeightsData = {};
        columns && Object?.keys(columns)?.map((column) => {

            var autoHeight = 0; // false
            
            // if no info saved, check if autoheight from server
            if(savedAutoHeights && Object.hasOwn(savedAutoHeights,column)){
                autoHeight = savedAutoHeights[column] ? savedAutoHeights[column] : 0;
            }
            else{

                // check info from server
                if(Object.hasOwn(columns[column],'autoheight')){
                    autoHeight = columns[column]?.autoheight ? columns[column]?.autoheight : 0;                    
                }
                else{
                    autoHeight = 0; // false
                }
            }

            autoHeightsData[column] = autoHeight;
        });

        setAutoHeights(autoHeightsData);

    }, [table]);

    // handle visibility
    const handleVisiblity = (e) => {

        const name = e.target.name;
        const value = e.target.checked;
        
        var newVisibility = {...visibility};
        newVisibility[name] = value ? 1 : 0;
        setVisibility(newVisibility);
    }

    // handle modal edition
    const handleModal = (e) => {

        //const name = e.target.name;
        const value = e.target.checked;

        setModalConfig(value ? 1 : 0);
    }

    // handle fit to screen
    const handleFit = (e) => {

        //const name = e.target.name;
        const value = e.target.checked;

        setFitConfig(value ? 1 : 0);

        // if true, change columns widths to auto
        if(value){
            const newSizes = {...sizes};
            Object.keys(newSizes).forEach((key) => {
                newSizes[key] = 0;
            });            
            setSizes(newSizes);
        }
    }

    // handle fix header
    const handleHeaderFixed = (e) => {

        //const name = e.target.name;
        const value = e.target.checked;

        setHeaderFixedConfig(value ? 1 : 0);
    }

    const handleSizeChange = (e, newValue) => {

        const name = e.target.name;
        const value = newValue;
        
        var newSizes = {...sizes};
        newSizes[name] = value;
        setSizes(newSizes);
    };

    const handleRowsHeightChange = (e, newValue) => {
        //const name = e.target.name;
        setRowsHeight(newValue);
    }

    const handleAutoHeight = (e) => {
        
        const name = e.target.name;
        const value = e.target.checked;

        var newAutoHeights = {...autoHeights};
        newAutoHeights[name] = value ? 1 : 0;
        setAutoHeights(newAutoHeights);
    };

    // save config
    const handleSaveConfig = () => {        

        // visibility
        // check at least one visible
        var visible = [];
        var invisible = [];
        visibility && Object.keys(visibility).map((column) => {
            if(visibility[column]){
                visible.push(column);
            }
            else{
                invisible.push(column);
            }
        });

        var errors = false;
        if(visible.length === 0){
            sendMessage(t('actions.config.errors.novisibilityselected'),'error');
            errors = true;
        }

        if(!errors){    

            var reload = false;        

            // visibility
            setTableConfig(api,user,table,'columnVisibility',visibility);
                    
            api?.setColumnsVisible(visible,true);
            api?.setColumnsVisible(invisible,false);

            // to fit the grid must have a minimum width 
            let fitConfigValue = fitConfig;              
            if(fitConfig){

                // check if can fit
                const canFit = checkCanFit(table,visible);

                if(!canFit){                    
                    
                    // check if coming from changing it to show message
                    const oldFit = getTableConfig(user,table,'fit');
                    
                    if(!oldFit){                            
                                        
                        sendMessage(t('actions.config.errors.cannotfittoscreen'),'error');

                        // change back the value
                        fitConfigValue = 0;
                        setFitConfig(0);
                    }

                    // fit to content               
                    //api.autoSizeAllColumns({scaleUpToFitGridWidth:true});
                }
                else{
                    // remove width from columns
                    /*
                    const currentColDefs = api.getColumnDefs();

                    const newColDefs = currentColDefs.map(colDef => {
                        const newDef = { ...colDef };                            
                        delete newDef.width; 
                        newDef.flex = 1;                             
                        return newDef;                        
                    });

                    api.setGridOption('columnDefs',newColDefs);
                    */
                    api.sizeColumnsToFit();
                }
            }
            else{
                // fit to content               
                //api.autoSizeAllColumns({scaleUpToFitGridWidth:true});
            }

            // check if fit to screen
            setTableConfig(api,user,table,'fit',fitConfigValue);

            // custom sizes
            setTableConfig(api,user,table,'columnSize',sizes);

            const sizesNew = [];
            //const autosizes = [];
            sizes && Object.keys(sizes).map((column) => {
                
                if(sizes[column]){
                    sizesNew.push({
                        key: column, 
                        newWidth: sizes[column]
                    });
                }
                else{
                    //autosizes.push(column);
                }
            });

            if(sizesNew.length > 0){
                api?.setColumnWidths(sizesNew);
            }
            //api.autoSizeColumns(autosizes);
            
            // modal edition. if modal changed, saved it and we need to refresh page            
            var modalInt = modal ? 1 : 0;
            if(modalInt !== modalConfig){
                setTableConfig(api,user,table,'modal',modalConfig);
                reload = true;
            }

            // to fix the header. check if changed
            if(headerFixed !== headerFixedConfig){
                setTableConfig(api,user,table,'headerFixed',headerFixedConfig);
                reload = true;
            }

            // save the heights

            setTableConfig(api,user,table,'rowsHeight',rowsHeight);

            api.setGridOption('rowHeight',rowsHeight ? rowsHeight : null);                

            // auto height for columns content
            setTableConfig(api,user,table,'columnAutoHeight',autoHeights);
            
            const colDefs = api?.getColumnDefs();
            colDefs && colDefs?.length > 0 && colDefs?.map((colDef,index) => {

                var column = colDef?.colId;

                var wrapText = false;
                var autoHeight = false;
                
                if(autoHeights && Object?.hasOwn(autoHeights,column) && autoHeights[column]){
                    wrapText = true;
                    autoHeight = true;
                }

                colDefs[index].wrapText = wrapText;
                colDefs[index].autoHeight = autoHeight;

            });

            api.setGridOption('columnDefs',colDefs);

            if(reload){
                window.location.reload();
            }            
            else{
                // close config modal
                setOpen(false);
            }
        }
    }

    // remove saved config and reload page to get the default config
    const handleResetConfig = () => {
        removeTableConfig(user,table);
        window.location.reload();
    }

    return (
        
        <Dialog
            container={fullScreen?.active ? fullScreen?.node?.current : null}
            key={'table_'+table+'_config_modal'}
            sx={sxDialog}
            onClose={handleCloseModal}
            open={open}
            fullWidth={true}
            maxWidth='md'
        >
            <DialogTitle className='modal-title'>
                {t('actions.config.title')}
            </DialogTitle>
            {/*
            <IconButton                    
                onClick={handleCloseModal}
                sx={sxCloseIcon}
            >
                <CloseIcon/>
            </IconButton> 
            */}
            <DialogContent className='modal-content'>

                <div className='font-bold border-b app-border-color-gradient'>
                    {t('actions.config.information')}
                </div>
                
                <div className='mt-2'>
                    {t('actions.config.description1')}
                </div>

                <div className='mt-2'>
                    {t('actions.config.description2')}
                </div>

                <div className='mt-8 font-bold border-b app-border-color-gradient'>
                    {t('actions.config.columnOrderTitle')}
                </div>
                
                <div className='mt-2'>
                    {t('actions.config.columnOrderDescription')}
                </div>

                {
                    (editable || enableAdd || enableEdit) ?
                        <>
                        <div className='mt-8 font-bold border-b app-border-color-gradient'>
                            {t('actions.config.modalTitle')}
                        </div>
                        
                        <div className='mt-2'>
                            {t('actions.config.modalDescription')}
                        </div>

                        <div>
                            <Switch    
                                sx={{
                                    color:
                                        modalConfig ? 
                                            darkmode ? darkColor : lightColor
                                        : 
                                            darkmode ? '#FFFFFF' : '#000000'                                    
                                }}          
                                fontWeight={modalConfig && 'bold'}          
                                name={table+'_modal'}
                                checked={modalConfig}
                                label={t('actions.config.modalSwitch')}
                                labelPlacement='end'                                                
                                onChange={handleModal}
                            />                                
                        </div>
                        </>
                    :
                        <></>
                }

                <div className='mt-8 font-bold border-b app-border-color-gradient'>
                    {t('actions.config.columnVisibilityTitle')}
                </div>
                
                <div className='mt-2'>
                    {t('actions.config.columnVisibilityDescription')}
                </div>

                <div className='mt-2'>
                    {
                        columns && Object?.keys(columns)?.map((column) => (
                            <div key={'table_'+table+'_column_'+column+'_visibility'}>
                                <Switch    
                                    sx={{
                                        color:
                                            visibility && Object.hasOwn(visibility,column) ? 
                                                visibility[column] ? 
                                                    darkmode ? darkColor : lightColor
                                                : 
                                                    darkmode ? '#FFFFFF' : '#000000' 
                                            : 
                                                darkmode ? '#FFFFFF' : '#000000'
                                    }}
                                    id={'table_'+table+'_column_'+column+'_visibility'}
                                    name={column}
                                    checked={visibility && Object.hasOwn(visibility,column) ? visibility[column] : false}
                                    label={t(table+'.fields.'+column)}
                                    labelPlacement='end'                                                
                                    onChange={handleVisiblity}
                                    fontSize='15px'
                                    fontWeight={visibility && Object.hasOwn(visibility,column) && visibility[column] && 'bold'}
                                />                                
                            </div>
                        ))
                    }
                </div>

                <div className='mt-8 font-bold border-b app-border-color-gradient'>
                    {t('actions.config.columnSizeTitle')}
                </div>

                <div className='mt-2'>
                    {t('actions.config.columnSizeDescription')}
                </div>

                <div className='mt-2 mx-4'>                    
                    <Switch   
                        sx={{
                            color:
                                fitConfig ? 
                                    darkmode ? darkColor : lightColor
                                : 
                                    darkmode ? '#FFFFFF' : '#000000'                                    
                        }}          
                        fontWeight={fitConfig && 'bold'}                     
                        name={table+'_fit'}
                        checked={fitConfig}
                        label={t('actions.config.fitSwitch')}
                        labelPlacement='end'                                                
                        onChange={handleFit}
                    />                                                
                </div>
                
                <div className='mt-4'>
                    {t('actions.config.columnSizeDescription2')}
                </div>

                <div className='mt-2 mx-4'>
                    {
                        columns && Object?.keys(columns)?.map((column) => (
                            <div key={'table_'+table+'_column_'+column+'_size'}>
                                <div className='font-bold app-color text-sm'>
                                    {t(table+'.fields.'+column)}: {sizes && Object.hasOwn(sizes,column) && sizes[column] ? sizes[column] : t('actions.config.autosize')}
                                </div>
                                <Slider
                                    sx={sxSlider}
                                    name={column}
                                    aria-label={column}
                                    //defaultValue={200}
                                    value={sizes && Object.hasOwn(sizes,column) ? sizes[column] : defaultSize}
                                    valueLabelFormat={(value) => value ? value : t('actions.config.autosize')}
                                    valueLabelDisplay="auto"                                    
                                    marks={marksSize}
                                    shiftStep={shiftStepSize}
                                    step={stepSize}
                                    min={minSize}
                                    max={maxSize}
                                    color="error"
                                    onChange={handleSizeChange}
                                    /*
                                    slots={{
                                        markLabel: (props) => 
                                            <SliderMarkLabel>
                                                <span>{props.ownerState.marks[props["data-index"]].label}</span>
                                            </SliderMarkLabel>
                                    }}
                                    */
                                    
                                    //valueLabelDisplay="on"                                    
                                    disabled={fitConfig}
                                />
                            </div>
                        ))
                    }
                </div>   

                <div className='mt-8 font-bold border-b app-border-color-gradient'>
                    {t('actions.config.headerFixedTitle')}
                </div>

                <div className='mt-2'>
                    {t('actions.config.headerFixedDescription')}
                </div>

                <div className='mt-2 mx-4'>                    
                    <Switch   
                        sx={{
                            color:
                                headerFixedConfig ? 
                                    darkmode ? darkColor : lightColor
                                : 
                                    darkmode ? '#FFFFFF' : '#000000'                                    
                        }}          
                        fontWeight={headerFixedConfig && 'bold'}                     
                        name={table+'_headerFixed'}
                        checked={headerFixedConfig}
                        label={t('actions.config.headerFixedSwitch')}
                        labelPlacement='end'                                                
                        onChange={handleHeaderFixed}
                    />                                                
                </div> 

                <div className='mt-8 font-bold border-b app-border-color-gradient'>
                    {t('actions.config.columnHeightTitle')}
                </div>

                <div className='mt-4'>
                    {t('actions.config.columnHeightDescription')}
                </div>

                <div className='mt-2 mx-4'>                    
                    <div key={'table_'+table+'_row_height'}>
                        <div className='font-bold app-color text-sm'>
                            {t('actions.config.height')}: {rowsHeight ? rowsHeight : t('actions.config.autoheight')}
                        </div>
                        <Slider
                            sx={sxSlider}
                            name={'height'}
                            aria-label={'height'}
                            //defaultValue={200}                            
                            value={rowsHeight !== null ? rowsHeight : defaultRowsHeight}
                            valueLabelFormat={(value) => value ? value : t('actions.config.autoheight')}
                            valueLabelDisplay="auto"                                    
                            marks={marksRowsHeight}
                            shiftStep={shiftStepRowsHeight}
                            step={stepRowsHeight}
                            min={minRowsHeight}
                            max={maxRowsHeight}
                            color="error"
                            onChange={handleRowsHeightChange}
                        />
                    </div>                                               
                </div>
                
                <div className='mt-4'>
                    {t('actions.config.columnHeightDescription2')}
                </div>

                <div className='mt-2'>
                    {
                        columns && Object?.keys(columns)?.map((column) => (
                            <div key={'table_'+table+'_column_'+column+'_autoheight'}>
                                <Switch    
                                    sx={{
                                        color:
                                            autoHeights && Object.hasOwn(autoHeights,column) ? 
                                                autoHeights[column] ? 
                                                    darkmode ? darkColor : lightColor
                                                : 
                                                    darkmode ? '#FFFFFF' : '#000000' 
                                            : 
                                                darkmode ? '#FFFFFF' : '#000000'
                                    }}
                                    id={'table_'+table+'_column_'+column+'_autoheight'}
                                    name={column}
                                    checked={autoHeights && Object.hasOwn(autoHeights,column) ? autoHeights[column] : false}
                                    label={t(table+'.fields.'+column)}
                                    labelPlacement='end'                                                
                                    onChange={handleAutoHeight}
                                    fontSize='15px'
                                    fontWeight={autoHeights && Object.hasOwn(autoHeights,column) && autoHeights[column] && 'bold'}
                                />                                
                            </div>
                        ))
                    }                
                </div>            
            </DialogContent>   
            <div className="modal-buttons">
                <div className='mr-1'>
                    <SaveIcon className='action-button-modal text-green-800' onClick={handleSaveConfig}/>
                </div>                
                <div className='mr-1'>
                    <ResetIcon className='action-button-modal text-red-600' onClick={handleResetConfig}/>
                </div>
                <div className=''>
                    <CloseIcon className='action-button-modal text-black' onClick={handleCloseModal}/>
                </div>
            </div>         
        </Dialog>
  );
}